/**
 * Mirror of /app/backend/core/followup_engine.py sequence definitions.
 *
 * Each entry describes:
 *   - the human title of the rule (R1..R11)
 *   - which user-funnel stage triggers it (with absolute trigger delay)
 *   - the variants present (A/B/C) and the sub-messages in each variant,
 *     with their CUMULATIVE delay from the trigger moment in seconds.
 *
 * Used by the /admin/messaggi editor to render rule-group headers and the
 * WhatsApp-style sequence previews. Keep in sync with the backend.
 */

export const RULES = {
  R1: {
    title: "Lead registrato MA password non impostata",
    triggerLabel: "+2 min dopo registrazione",
    summary: "3 varianti A/B/C · 4 sotto-messaggi ciascuna",
    variants: {
      A: { hint: 'Opener "201° genitore" upfront', tags: ["msg_pwd_a1", "msg_pwd_a2", "msg_pwd_a3", "msg_pwd_a4"] },
      B: { hint: "Opener corto, sobrio",            tags: ["msg_pwd_b1", "msg_pwd_b2", "msg_pwd_b3", "msg_pwd_b4"] },
      C: { hint: 'Pattern interrupt: "qual\'è quella cosa…"', tags: ["msg_pwd_c1", "msg_pwd_c2", "msg_pwd_c3", "msg_pwd_c4"] },
    },
    delays: [0, 10, 25, 29],  // cumulative seconds from trigger
  },
  R2: {
    title: "Password impostata, Lezione 1 non avviata — lead vergine",
    triggerLabel: "+5 min dopo set-password (no msg precedente)",
    summary: "2 varianti A/B · 4 sotto-messaggi ciascuna",
    variants: {
      A: { hint: 'Msg 2 enfatizza "201° genitore"', tags: ["msg_l1_a_a1", "msg_l1_a_a2", "msg_l1_a_a3", "msg_l1_a_a4"] },
      B: { hint: "Msg 2 sobrio",                    tags: ["msg_l1_a_b1", "msg_l1_a_b2", "msg_l1_a_b3", "msg_l1_a_b4"] },
    },
    delays: [0, 10, 25, 29],
  },
  R3: {
    title: "Password impostata, L1 non avviata — già toccato",
    triggerLabel: "+5 min dopo set-password (LM ≠ ∅)",
    summary: "Singolo messaggio",
    variants: { _: { hint: "Tono familiare, link a L1", tags: ["msg_l1_b"] } },
    delays: [0],
  },
  R4: {
    title: "L1 avviata MA <80% — lead vergine",
    triggerLabel: "+15 min dopo lesson_1_started (¬LM)",
    summary: "3 sotto-messaggi",
    variants: { _: { hint: "Sobrio, chiede feedback + link L1", tags: ["msg_fb_a_1", "msg_fb_a_2", "msg_fb_a_3"] } },
    delays: [0, 10, 20],
  },
  R5: {
    title: "L1 avviata MA <80% — già toccato (LM = pwd/L1-not-started)",
    triggerLabel: "+15 min dopo lesson_1_started",
    summary: "2 sotto-messaggi",
    variants: { _: { hint: 'Pattern "Come va? Come stai?"', tags: ["msg_fb_b_1", "msg_fb_b_2"] } },
    delays: [0, 10],
  },
  R6: {
    title: "L1 avviata MA <80% — LM altro",
    triggerLabel: "+15 min dopo lesson_1_started",
    summary: "2 sotto-messaggi (gap 30s)",
    variants: { _: { hint: "Domanda diretta su applicabilità + meta-feedback", tags: ["msg_fb_c1", "msg_fb_c2"] } },
    delays: [0, 30],
  },
  R7: {
    title: "L1 80% raggiunto MA L2 non richiesta — lead vergine",
    triggerLabel: "+5 min dopo 80% L1 (¬LM)",
    summary: "4 sotto-messaggi con link bundle gratuito",
    variants: { _: { hint: "Offre bundle L2-3-4 gratis", tags: ["msg_l2_1", "msg_l2_2", "msg_l2_3", "msg_l2_4"] } },
    delays: [0, 10, 20, 23],
  },
  R8: {
    title: "L1 80% MA L2 non richiesta — LM = fb_b/fb_c",
    triggerLabel: "+5 min dopo 80% L1",
    summary: "2 sotto-messaggi",
    variants: { _: { hint: '"Come va?" + bundle L2-3-4', tags: ["msg_l2_fb_1", "msg_l2_fb_2"] } },
    delays: [0, 10],
  },
  R9: {
    title: "L1 80% MA L2 non richiesta — LM = pwd/L1-not-started",
    triggerLabel: "+5 min dopo 80% L1",
    summary: "2 sotto-messaggi",
    variants: { _: { hint: '"Come stai?" + bundle L2-3-4', tags: ["msg_l2_pwd_1", "msg_l2_pwd_2"] } },
    delays: [0, 10],
  },
  R10: {
    title: "L2 richiesta sbloccare — già toccato",
    triggerLabel: "+2 min dopo richiesta sblocco L2 (LM ≠ ∅)",
    summary: "Singolo messaggio (hot lead, bypass consent)",
    variants: { _: { hint: 'Diretto: "quando avresti 2 minuti?"', tags: ["msg_l2_req_a"] } },
    delays: [0],
  },
  R11: {
    title: "L2 richiesta sbloccare — lead vergine",
    triggerLabel: "+2 min dopo richiesta sblocco L2 (LM = ∅)",
    summary: "2 sotto-messaggi (gap 15s)",
    variants: { _: { hint: "Presentazione + richiesta call", tags: ["msg_l2_req_b1", "msg_l2_req_b2"] } },
    delays: [0, 15],
  },
};

// Quick lookup: tag → { rule, variant, index, delaySec }
export const TAG_INDEX = (() => {
  const idx = {};
  Object.entries(RULES).forEach(([rule, def]) => {
    Object.entries(def.variants).forEach(([variant, vdef]) => {
      vdef.tags.forEach((tag, i) => {
        idx[tag] = { rule, variant: variant === "_" ? null : variant, index: i, delaySec: def.delays[i] ?? 0, total: vdef.tags.length };
      });
    });
  });
  return idx;
})();

export function formatDelay(sec) {
  if (sec === 0) return "subito";
  if (sec < 60) return `+${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `+${m}m${s}s` : `+${m}min`;
}
